import DOMPurify from 'dompurify';
import { Marked } from 'marked';

// The editor's valid_elements drops anything else without a word, so the
// conversion targets exactly what it accepts.
const BLOCK_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'sup',
  'ul',
  'ol',
  'li',
];

const INLINE_TAGS = ['strong', 'b', 'em', 'i', 'u', 's', 'strike', 'sup'];

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

const converter = new Marked({
  gfm: true,
  renderer: {
    heading({ tokens }) {
      return `<p><strong>${this.parser.parseInline(tokens)}</strong></p>`;
    },
    blockquote({ tokens }) {
      return this.parser.parse(tokens);
    },
    code({ text }) {
      return `<p>${escapeHtml(text)}</p>`;
    },
    codespan({ text }) {
      return escapeHtml(text);
    },
    link({ tokens }) {
      return this.parser.parseInline(tokens);
    },
    image({ text }) {
      return escapeHtml(text || '');
    },
    hr() {
      return '';
    },
    table(token) {
      const rows = [token.header, ...token.rows];
      return rows
        .map(
          row =>
            `<p>${row
              .map(cell => this.parser.parseInline(cell.tokens))
              .join(' — ')}</p>`,
        )
        .join('');
    },
  },
});

function normalize(text: string): string {
  return (text || '').replace(/\r\n?/g, '\n');
}

export function markdownToHtml(text: string): string {
  const html = converter.parse(normalize(text), { async: false });
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: BLOCK_TAGS,
    ALLOWED_ATTR: [],
  })
    .replace(/>\n+</g, '><')
    .trim();
}

export function markdownToInline(text: string): string {
  const flattened = markdownToHtml(text).replace(
    /<\/(p|li|h[1-6])>|<br\s*\/?>/g,
    ' ',
  );

  return DOMPurify.sanitize(flattened, {
    ALLOWED_TAGS: INLINE_TAGS,
    ALLOWED_ATTR: [],
  })
    .replace(/\s+/g, ' ')
    .trim();
}
